<template>
  <page-view
    title="Live Video Shopping"
    icon="shopping-music"
    :data-full-content="true"
    :class="featuresEnabled.includes('ppv') && (featuresEnabled.includes('lvs') && lvsEditorAccessable) ? 'subpage' : ''"
    :largeMaxWidth="true"
    :backLink="featuresEnabled.includes('ppv') && (featuresEnabled.includes('lvs') && lvsEditorAccessable) ? '/appearance' : ''"
    :backText="featuresEnabled.includes('ppv') && (featuresEnabled.includes('lvs') && lvsEditorAccessable) ? 'Back to Appearance ': ''"
  >
    <template v-slot:header-right>
      <p @click="sidebar = !sidebar">Hide sidebar</p>
      <Button :class="`${saveState === 'saved' ? 'green' : ''}`" :disabled="saveState !== ''" :loading="saveState === 'saving'" @click="save">{{ saveState === 'saved' ? 'Saved' : 'Save' }}</Button>
    </template>
    <div class="videoplayer-settings" :data-show-sidebar="sidebar">
      <section>
        <div class="browser-window" :data-mobile="mobile" :data-player-focus="focus">
          <div class="top"><span class="dot"></span><span class="dot"></span><span class="dot"></span></div>
          <div class="browser-content nice-scrollbar">
            <iframe src="/preview.html" data-broadcast="g6F38Tel" data-ignore-iframe="true" ref="iframe" />
          </div>
        </div>
      </section>
      <aside>
        <ul class="player-category">
          <li :data-active="tab === 'poster'" @click="changeTab('poster')">Poster</li>
          <li :data-active="tab === 'brand'" @click="changeTab('brand')">Brand</li>
          <li :data-active="tab === 'products'" @click="changeTab('products')">Products</li>
          <li :data-active="tab === 'chat'" @click="changeTab('chat')">Chat</li>
        </ul>
        <div class="p-4" v-for="(row, index) in properties[tab]" :key="index">
          <strong class="f-1">{{ row.label }}</strong>
          <template v-if="row.cssvars">
            <div :class="`d-f f-g-16 f-3 ${row.ownRow ? 'fd-c' : 'f-wrap'}`">
              <div class="d-f f-g-8 ai-c h-w a-f-1" v-for="(prop, pindex) in row.props" :key="pindex">
                <p v-if="prop.name">{{ prop.name }}</p>
                <template v-if="prop.type === 'color'">
                  <Color v-bind="prop.props" :value="css[prop.var]" :key="prop.var" @color="setProperty(prop.var, $event)" />
                </template>
                <template v-else-if="['input', 'number'].includes(prop.type)">
                  <FormInput class="smallish ml-a mono" v-bind="prop.props" :value="prop.props?.stepper ? parseInt(css[prop.var], 10) : css[prop.var]" @input="setProperty(prop.var, `${$event}px`)" />
                </template>
                <template v-else-if="prop.type === 'dropdown'">
                  <FormDropdown :items="prop.items" :key="formKeys" @input="setProperty(prop.var, $event.value)" />
                </template>
              </div>
            </div>
          </template>
          <template v-else>
            <component @[row.component.event]="row.component.eventFunc" :is="row.component.type" v-bind="row.component.props" />
          </template>
        </div>
      </aside>
    </div>
  </page-view>
</template>

<script>
import Color from '@/components/Form/Color.vue';
import MediaUpload from '@/components/MediaUpload.vue';

export default {
  components: {
    Color,
    MediaUpload,
  },
  computed: {
    /**
     * Return boolean if lvs editor is accessable
     * @returns {Boolean}
     */
    lvsEditorAccessable() {
      const trialEnded = this.$store.getters['AccountManager/trialEnded'];
      const subscriptionEnded = this.$store.getters['AccountManager/subscriptionEnded'];

      if (trialEnded || subscriptionEnded) {
        return false;
      }

      return this.accountSettings?.shopping?.permissions?.appearance;
    },
    /**
     * Return which part to focus on the player
     */
    focus() {
      if (this.playerState === 'unopen') {
        return 'none';
      }

      if (this.playerState === 'open') {
        return this.tab;
      }

      return 'none';
    },
    config() {
      return this.$store.getters['Play/lvspConfig'];
    },
    allLoaded() {
      return this.configLoaded && this.iframeLoaded;
    },
    properties() {
      // eslint-disable-next-line
      const { formKeys } = this;
      return {
        poster: [
          {
            label: 'Title text',
            cssvars: true,
            props: [
              { var: '--broadcast-tile-title-color', name: 'Color', type: 'color', default: '#ffffff', props: { noalpha: true, noInput: true } },
            ],
          },
          {
            label: 'Date text',
            cssvars: true,
            props: [
              { var: '--broadcast-tile-time-color', name: 'Color', type: 'color', default: '#ffffffb3', props: { noInput: true } },
            ],
          },
          {
            label: 'Live badge',
            cssvars: true,
            props: [
              { var: '--live-background', name: 'Background', type: 'color', default: '#E80200', props: { noInput: true } },
              { var: '--live-color', name: 'Text', type: 'color', default: '255, 255, 255', props: { noInput: true, returnRGB: true } },
            ],
          },
          {
            label: 'Play icon',
            cssvars: true,
            props: [
              { var: '--play-icon-background', name: 'Background', type: 'color', default: '#FFFFFF', props: { noInput: true } },
              { var: '--play-icon-color', name: 'Text', type: 'color', default: '#000000', props: { noInput: true } },
            ],
          },
          {
            label: 'Play icon shadow',
            cssvars: true,
            props: [
              { var: '--play-icon-shadow', name: 'Color', type: 'color', default: '0, 0, 0', props: { noInput: true, returnRGB: true } },
              { var: '--play-icon-shadow-hover', name: 'Hover', type: 'color', default: '255, 255, 255', props: { noInput: true, returnRGB: true } },
            ],
          },
          {
            label: 'Poster image',
            cssvars: true,
            props: [
              { var: '--poster-image-border-radius', name: 'Border radius', type: 'input', default: '12px', props: { noInput: true, stepper: true, type: 'number', step: 1, fixedPlaceholder: 'px' } },
              { var: '--poster-image-background', name: 'Default background', type: 'color', default: '#294392', props: { noInput: true } },
            ],
          },
        ],
        brand: [
          {
            label: 'Logo overlay',
            cssvars: false,
            component: {
              type: 'MediaUpload',
              event: 'images',
              eventFunc: (arr) => {
                this.logo = arr.length === 0 ? '' : arr[0].value;
                this.$refs.iframe.contentWindow.dispatchEvent(new CustomEvent('logochange', { detail: this.logo }));
              },
              props: {
                style: 'max-width: 250px;',
                chessboard: true,
                multiple: false,
                title: 'Logo',
                overwrite: true,
                size: 'h/50',
                data: {
                  force: true,
                  images: [],
                },
              },
            },
          },
          {
            label: 'Fonts',
            cssvars: true,
            ownRow: true,
            props: [
              { var: '--liveshopping-font-default', name: 'Main', type: 'input', default: '"Inter", sans-serif' },
            ],
          },
          {
            label: 'Modal',
            cssvars: true,
            ownRow: false,
            props: [
              { var: '--modal-border-radius', name: 'Border radius', type: 'input', default: '12px', props: { noInput: true, stepper: true, type: 'number', step: 1, fixedPlaceholder: 'px' } },
              { var: '--modal-background', name: 'Background', type: 'color', default: '#FFFFFF', props: { noInput: true } },
              { var: '--modal-backdrop-color', name: 'Backdrop background', type: 'color', default: '#00000055', props: { noInput: true } },
            ],
          },
          {
            label: 'Title text',
            cssvars: true,
            ownRow: false,
            props: [
              { var: '--title-color', name: 'Color', type: 'color', default: '#000000', props: { noInput: true } },
              { var: '--title-font-size', name: 'Font size', type: 'input', default: '12px', props: { noInput: true, stepper: true, type: 'number', step: 1, fixedPlaceholder: 'px' } },
              { var: '--title-size', name: 'Line height', type: 'input', default: '22px', props: { noInput: true, stepper: true, type: 'number', step: 1, fixedPlaceholder: 'px' } },
              { var: '--title-weight', name: 'Weight', type: 'dropdown', default: '400', items: Array.from({ length: 9 }, (_, i) => ({ selected: this.css['--title-weight'] === (i + 1) * 100 || this.css['--title-weight'] === `${(i + 1) * 100}`, value: (i + 1) * 100, string: (i + 1) * 100 })), props: { noInput: true } },
            ],
          },
          {
            label: 'Minified player',
            cssvars: true,
            ownRow: false,
            props: [
              { var: '--modal-minified-top', name: 'Top', type: 'input', default: 'unset', props: { noInput: true } },
              { var: '--modal-minified-right', name: 'Right', type: 'input', default: '16px', props: { noInput: true } },
              { var: '--modal-minified-bottom', name: 'Bottom', type: 'input', default: '16px', props: { noInput: true } },
              { var: '--modal-minified-left', name: 'Left', type: 'input', default: 'unset', props: { noInput: true } },
              { var: '--modal-minified-left', name: 'Left', type: 'input', default: '12px', props: { noInput: true, stepper: true, type: 'number', step: 1, fixedPlaceholder: 'px' } },
            ],
          },
          /*
          --title-font-size: 14px;
          --title-letter-spacing: 0.36px;
          --title-size: 22px
          --title-color: #000000;
          --title-weight: 400;
          --modal-border-radius: 12px;
          --modal-backdrop-color: #00000055;
          --modal-box-background:#FFFFFF;
          --modal-minified-top: unset;
          --modal-minified-left: unset;
          --modal-minified-right: 16px;
          --modal-minified-bottom: 16px;
          --title-font-size: 14px;
          --title-letter-spacing: 0.36px;
          --title-size: 22px
          --title-color: #000000;
          --title-weight: 400;
          */
        ],
        products: [
          {
            label: 'Product cards',
            cssvars: false,
            ownRow: true,
            component: {
              type: 'FormRadio',
              event: 'input',
              eventFunc: ({ value }) => {
                this.productsGrid = value;
                this.$refs.iframe.contentWindow.dispatchEvent(new CustomEvent('productlistchange', { detail: this.productsGrid }));
              },
              props: {
                items: [
                  { value: false, string: 'List', selected: this.productsGrid === false },
                  { value: true, string: 'Grid', selected: this.productsGrid === true },
                ],
              },
            },
          },
          {
            label: 'Title',
            cssvars: true,
            ownRow: false,
            props: [
              { var: '--product-title-font-default', name: 'Font', type: 'input', default: '"Inter", sans-serif' },
              { var: '--product-title-color', name: 'Price', type: 'color', default: '#000000', props: { noInput: true } },
              { var: '--product-title-size', name: 'Size', type: 'input', default: '16px', props: { noInput: true, stepper: true, type: 'number', step: 1, fixedPlaceholder: 'px' } },
              { var: '--product-title-weight', name: 'Weight', type: 'dropdown', default: '600', items: Array.from({ length: 9 }, (_, i) => ({ selected: this.css['--product-title-weight'] === (i + 1) * 100 || this.css['--product-title-weight'] === `${(i + 1) * 100}`, value: (i + 1) * 100, string: (i + 1) * 100 })), props: { noInput: true } },
              { var: '--product-title-letter-spacing', name: 'Letter spacing', type: 'input', default: '0', props: { noInput: true, stepper: true, type: 'number', step: 1, fixedPlaceholder: 'px' } },
              { var: '--product-title-transform', name: 'Transform', type: 'dropdown', default: 'none', items: [
                { selected: this.css['--product-title-transform'] === 'capitalize', value: 'capitalize', string: 'Capitalize'},
                { selected: this.css['--product-title-transform'] === 'uppercase', value: 'uppercase', string: 'Uppercase'},
                { selected: this.css['--product-title-transform'] === 'lowercase', value: 'lowercase', string: 'Lowercase'},
                { selected: this.css['--product-title-transform'] === 'none', value: 'none', string: 'None'},
              ]},
            ],
          },
          {
            label: 'List images',
            cssvars: true,
            props: [
              { var: '--list-product-image-padding', name: 'Padding', type: 'input', default: '0px', props: { noInput: true, stepper: true, min: 0, type: 'number', step: 1, fixedPlaceholder: 'px' } },
              { var: '--list-product-image-size', name: 'Size', type: 'input', default: '96px', props: { noInput: true, stepper: true, type: 'number', step: 1, fixedPlaceholder: 'px' } },
              { var: '--product-image-fit', name: 'Fit', type: 'dropdown', default: 'cover', items: [
                { selected: this.css['--product-image-fit'] === 'cover', value: 'cover', string: 'Cover'},
                { selected: this.css['--product-image-fit'] === 'contain', value: 'contain', string: 'Contain'},
                { selected: this.css['--product-image-fit'] === 'fill', value: 'fill', string: 'Fill'},
                { selected: this.css['--product-image-fit'] === 'none', value: 'none', string: 'None'},
              ]},
            ],
          },
          {
            label: 'Product details',
            cssvars: false,
            ownRow: true,
            component: {
              type: 'FormRadio',
              event: 'input',
              eventFunc: ({ value }) => {
                this.productDetailsAlign = value;
                this.setProperty('--product-title-align', value);
                this.setProperty('--product-pricing-justify', value);
              },
              props: {
                items: [
                  { value: 'flex-start', string: 'Left', selected: this.productDetailsAlign === 'flex-start' },
                  { value: 'center', string: 'Center', selected: this.productDetailsAlign === 'center' },
                ],
              },
            },
          },
          {
            label: 'Price colors',
            cssvars: true,
            props: [
              { var: '--product-price-color', name: 'Price', type: 'color', default: '#000000', props: { noInput: true } },
              { var: '--product-sales-price-color', name: 'Sale', type: 'color', default: '#DF1B12', props: { noInput: true } },
            ],
          },
        ],
        chat: [
          {
            label: 'Messages',
            cssvars: true,
            props: [
              { var: '--chat-message-size', name: 'Size', type: 'input', default: '12px', props: { noInput: true } },
              { var: '--chat-message-height', name: 'Line height', type: 'input', default: '18px', props: { noInput: true } },
              { var: '--chat-message-user-color', name: 'User color', type: 'color', default: '#000000', props: { noInput: true } },
            ],
          },
          {
            label: 'Moderator',
            cssvars: true,
            props: [
              { var: '--chat-message-moderator-background', name: 'Background', type: 'color', default: '#000000', props: { noInput: true } },
              { var: '--chat-message-moderator-color', name: 'Color', type: 'color', default: '#FFFFFF', props: { noInput: true } },
            ],
          },
          {
            label: 'Pinned message',
            cssvars: true,
            props: [
              { var: '--pinned-message-background', name: 'Background', type: 'color', default: '#FFA850', props: { noInput: true } },
              { var: '--pinned-message-color', name: 'Color', type: 'color', default: '#000000', props: { noInput: true } },
              { var: '--pinned-message-size', name: 'Size', type: 'input', default: '12px', props: { noInput: true, stepper: true, type: 'number', step: 1, fixedPlaceholder: 'px' } },
            ],
          },
        ],
      };
    },
  },  
  methods: {
    changeTab(tab) {
      this.tab = tab;
      try {
        const iframeDocument = this.$refs.iframe.contentDocument || this.$refs.iframe.contentWindow.document;

        if (tab === 'poster') {
          if (this.playerState !== 'unopen') {
            const themodal = iframeDocument.querySelector('streamify-modal').shadowRoot.querySelector('sf-modal-inner');
            const clickEvent = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
            });
            themodal.dispatchEvent(clickEvent);
          }
          return;
        }

        if (this.playerState === 'unopen') {
          if (iframeDocument.querySelector('streamify-modal')) {
            const themodal = iframeDocument.querySelector('streamify-modal').shadowRoot.querySelector('sf-player-inner');
            const clickEvent = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
            });
            themodal.dispatchEvent(clickEvent);
            return;
          }
          const theposter = iframeDocument.querySelector('streamify-liveshopping').shadowRoot.querySelector('.liveshopping').firstChild;
          const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
          });
          theposter.dispatchEvent(clickEvent);
        }
      } catch (e) {
        // Do nothing
      }
    },
    setProperty(property, color) {
      this.iframeDocument.querySelector('streamify-liveshopping').style.setProperty(property, color);
      this.iframeDocument.querySelector('streamify-modal').style.setProperty(property, color);
      this.css[property] = color;
      this.formKeys += 1;
    },
    async save() {
      this.saveState = 'saving';
      const { css: appearance, logo } = this;

      const payload = {
        images: {
          logo,
        },
        appearance,
      };

      const result = await this.$store.dispatch('Play/updateLVSPConfig', payload);
      this.saveState = 'saved';

      setTimeout(() => {
        this.saveState = '';
      }, 2000);
    },
    setCSSVars() {
      this.iframeDocument = this.$refs.iframe.contentDocument || this.$refs.iframe.contentWindow.document;
      const style = document.createElement('style');
      style.dataset.cssenvs = 'yes';
      this.styleElement = style;
      style.innerHTML = 'streamify-liveshopping, streamify-modal {';
      // eslint-disable-next-line
      Object.entries(this.css).forEach(([property, value]) => {
        style.innerHTML += `${property}: ${value};`;
      });
      style.innerHTML += '}';

      this.iframeDocument.getElementsByTagName('head')[0].appendChild(style);

      this.formKeys += 1;

      setTimeout(() => {
        const themodal = this.iframeDocument.querySelector('streamify-liveshopping').shadowRoot.querySelector('.poster').firstChild;
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
        });
        themodal.dispatchEvent(clickEvent);
      }, 1000);
    },
  },
  data() {
    return {
      configLoaded: false,
      iframeLoaded: false,
      saveState: '',
      mobile: false,
      sidebar: true,
      tab: 'products',
      playerState: 'unopen',
      styleElement: null,
      iframeDocument: null,
      productsGrid: false,
      productDetailsAlign: 'center',
      logo: '',
      formKeys: 0,
      css: {
      },
    };
  },
  watch: {
    config: {
      immediate: true,
      handler(val) {
        // Load default values first
        // eslint-disable-next-line
        Object.entries(this.properties).forEach(([, value]) => value.forEach(props => props.cssvars ? props.props.forEach((p) => {
          this.css[p.var] = p.default;
        }) : null));

        this.logo = val.images.logo;
        this.properties.brand[0].component.props.data.images = [val.images.logo];

        Object.entries(val.appearance).forEach(([property, value]) => {
          if (property.slice(0, 2) === '--') {
            this.css[property] = value;
          }
        });
        this.configLoaded = true;

        console.log(this.css);
      },
    },
    allLoaded() {
      this.setCSSVars();
    },
  },
  mounted() {
    this.$el.querySelector('iframe').addEventListener('load', () => {
      this.iframeLoaded = true;
    }, { once: true });

    window.addEventListener('message', async (e) => {
      try {
        const data = JSON.parse(e.data);
        switch (data.action) {
          case 'liveshopping.open':
            this.playerState = 'open';
            break;
          case 'liveshopping.reopen':
            this.playerState = 'open';
            break;
          case 'liveshopping.minimize':
            this.playerState = 'unopen';
            break;
          default:
            break;
        }
      // eslint-disable-next-line
      } catch (e) { /* Do nothing */ }
    });
  },
};
</script>

<style lang="scss" scoped>

.player-category {
  font-family: Menlo, Consolas, monospace;
  display: flex;
  justify-content: space-around;

  li {
    cursor: pointer;

    &[data-active="true"] {
      border-bottom: 1px solid #1643c9;
    }
  }
}

.videoplayer-settings {
  flex: 1;
  position: relative;
  display: flex;

  section {
    width: 100%;
    overflow: hidden;
    padding: 5px;
    display: flex;
    align-items: center;
    transition: all .3s;
    container-type: inline-size;
  }

  aside {
    background-color: white;
    box-shadow: 0 6px 12px 0 rgba(0, 0, 0, 0.04), 0 2px 4px 0 rgba(0, 0, 0, 0.04);

    position: absolute;
    top: 0;
    right: 0;
    //bottom: 0;
    width: 100%;
    min-height: 100%;
    max-width: 530px;
    transition: all .3s;
    transform: translateX(530px);
  }

  &[data-show-sidebar="true"] {
    aside {
      transform: translateX(0);
    }

    @media (min-width: 1024px) {
      section {
        width: calc(100% - 530px);
      }
    }
  }
}

.browser-window {
  border: 3px solid #d8d8d8;
  border-radius: 4px;
  display: inline-flex;
  flex-direction: column;
  margin: 0 auto;
  width: 1280px;
  height: 928px;

  .top {
    padding: 10px;
    background-color: #d8d8d8;
    border-radius: 0 0 0 0;
  }

  .dot {
    height: 12px;
    width: 12px;
    background-color: #bbbbbb;
    border-radius: 6px;
    display: inline-block;

    &:not(:last-child) {
      margin-right: 5px;
    }
  }

  .browser-content {
    flex: 1;
    overflow: hidden;
    position: relative;
    background-color: white;
    border-radius: 0 0 3px 3px;

    iframe {
      width: 1450px;
      height: 884px;
      position: absolute;
      top: 0;
      bottom: 0;
      transition: all .3s;
    }
  }

  &[data-mobile="true"] {
    height: 844px;
    width: 390px;
    margin: auto auto;

    iframe {
      width: 100%;
      height: 100%;
    }
  }

  &[data-player-focus="none"] {
    iframe {
      width: 100%;
      height: 100%;
    }
  }
}
.videoplayer-settings {
  @container (width >= 1280px) {
    .browser-window {
      width: 100%;

      &[data-mobile="true"] {
        height: 860px;
        width: 394px;
      }
    }
    .browser-content {
      iframe {
        width: 100%;
        height: 100%;
        min-height: 880px;
      }
    }

    .browser-window {
      &[data-mobile="true"] {
        .browser-content {
          iframe {
            min-height: auto;
          }
        }
      }
    }
  }

  @container (width < 1280px) {
    .browser-window {
      &:not([data-mobile="true"]) {
        &[data-player-focus="brand"] {
          iframe {
            right: calc(calc(-1450px / 2) + 50%);
          }
        }

        &[data-player-focus="products"] {
          iframe {
            right: 0;
          }
        }
      }
    }
  }
}
</style>
